import { taskAssignmentTargetId } from "routes/skill-mapper/server/schedule-assignment-routing.mjs"
import { shortFolderName } from "./build-assignment-board"

/**
 * Where a task's trade comes from, in the same order the cost engine decides it: the
 * task's own assignment, then its schedule's, then a folder's, then skill routing. Shown
 * under each task in the schedule drawer so the user can see why it goes where it does,
 * and, for assigned work, what the assignment overrides.
 *
 * Conditional skill rules can depend on the facility or asset, so the drawer can't say
 * which applies to a given task. Assigned work lists the ones it ignores (those sending
 * work to another trade); skill-routed work names them as possible exceptions.
 *
 * @param {{
 *   task: { taskId: string, skillId: string, skillName?: string },
 *   scheduleId: string,
 *   board: { taskAssignments: Map, scheduleAssignments: Map, schedulesById?: Map, linesBySkill: Map },
 *   trades: Object<string, string>,
 * }} input
 * @returns {{
 *   source: "task" | "schedule" | "folder" | "skill" | "none",
 *   tradeName: string | null,
 *   route: string,
 *   overrides: string[],
 *   conditionalRules: string[],
 * }}
 *   route says how the trade is reached, e.g. "Assigned through folder: Boilers"; overrides
 *   lists what an assignment takes precedence over, e.g. "Overrides skill default: Mechanical"
 */
export function describeTaskRouting({ task, scheduleId, board, trades }) {
    const scheduleAssignment = board.scheduleAssignments.get(scheduleId)
    const line = task.skillId ? board.linesBySkill.get(String(task.skillId)) : null
    const taskAssignment = board.taskAssignments.get(taskAssignmentTargetId(scheduleId, task.taskId))
    if (taskAssignment) {
        return assigned("task", taskAssignment, "Set on this task", overriddenAssignment(scheduleAssignment))
    }

    if (scheduleAssignment?.kind === "schedule") {
        const code = board.schedulesById?.get(scheduleId)?.code
        const route = code ? `Assigned through schedule: ${code}` : "Assigned through this schedule"
        return assigned("schedule", scheduleAssignment, route)
    }
    if (scheduleAssignment?.kind === "folder") {
        return assigned(
            "folder",
            scheduleAssignment,
            `Assigned through folder: ${shortFolderName(scheduleAssignment.name)}`
        )
    }

    if (!task.skillId) return none("No trade: the task has no skill")
    const skillName = task.skillName || "this skill"
    const conditionalRules = (line?.conditionalTrades ?? []).map(toRuleName).filter(Boolean)
    if (!trades[line?.tradeId]) {
        return { ...none(`No trade: ${skillName} is not routed on this skill map`), source: "skill", conditionalRules }
    }
    return {
        source: "skill",
        tradeName: trades[line.tradeId],
        route: `From skill routing for ${skillName}`,
        overrides: [],
        conditionalRules,
    }

    function assigned(source, assignment, route, extra = []) {
        const overrides = [...extra, ...overriddenSkillRouting(assignment.tradeId)]
        return { source, tradeName: trades[assignment.tradeId], route, overrides, conditionalRules: [] }
    }

    // A task's own assignment takes precedence over its schedule's or folder's, when that
    // would send it somewhere else
    function overriddenAssignment(assignment) {
        if (!assignment || assignment.tradeId === taskAssignment.tradeId) return []
        const what =
            assignment.kind === "folder"
                ? `folder assignment (${shortFolderName(assignment.name)})`
                : "schedule assignment"
        return [`Overrides ${what}: ${trades[assignment.tradeId]}`]
    }

    // Skill routing that would have sent the task to another trade
    function overriddenSkillRouting(tradeId) {
        const overrides = []
        if (trades[line?.tradeId] && line.tradeId !== tradeId) {
            overrides.push(`Overrides skill default: ${trades[line.tradeId]}`)
        }
        for (const rule of line?.conditionalTrades ?? []) {
            if (!rule?.name || rule.tradeId === tradeId) continue
            const target = trades[rule.tradeId]
            // A rule may never have fired for this task (it can depend on the site), so the
            // assigned work ignores it rather than overriding it
            overrides.push(`Ignores conditional rule: ${rule.name}${target ? ` (${target})` : ""}`)
        }
        return overrides
    }
}

function none(route) {
    return { source: "none", tradeName: null, route, overrides: [], conditionalRules: [] }
}

function toRuleName(rule) {
    return rule?.name
}
